import type { ProposalQuote, UsdRange } from "./types";
import { placeKey } from "./city-names";

/** What an event's proposals say a city costs, as ranges across its quoting venues. USD. */
export interface ProposalRates {
  /** Venues with a quote in the city. */
  count: number;
  roomPerNight: UsdRange | null;
  /** Meeting space for the whole event. */
  meetingTotal: UsdRange | null;
  /** Food and beverage for the whole event. */
  foodTotal: UsdRange | null;
}

const rangeOf = (values: Array<number | null>): UsdRange | null => {
  const known = values.filter((value): value is number => value !== null && Number.isFinite(value) && value > 0);
  return known.length ? { low: Math.min(...known), high: Math.max(...known) } : null;
};

/**
 * Groups an event's venue quotes by city into the ranges the estimate prices with.
 * @param quotes - The event's live venue quotes.
 * @returns Rates keyed by {@link placeKey}.
 * @throws Never.
 */
export const proposalRatesByCity = (quotes: readonly ProposalQuote[]): Map<string, ProposalRates> => {
  const byCity = new Map<string, ProposalQuote[]>();
  for (const quote of quotes) {
    // A venue in "Roma" counts for the destination "Rome".
    const key = placeKey(quote.city);
    byCity.set(key, [...(byCity.get(key) ?? []), quote]);
  }
  return new Map(
    [...byCity].map(([key, group]) => [
      key,
      {
        count: group.length,
        roomPerNight: rangeOf(group.map((quote) => quote.roomRateUsd)),
        meetingTotal: rangeOf(group.map((quote) => quote.meetingTotalUsd)),
        foodTotal: rangeOf(group.map((quote) => quote.foodTotalUsd)),
      },
    ])
  );
};
